import { useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styles from './OfftakeAddressDetails.module.scss'
import { Banner, Button, Heading, Icon, Modal } from '@boltenergy-be/design-system'
import { routes } from 'types/routes.ts'
import parse from 'html-react-parser'
import { isInactiveContract } from 'utils/contracts.ts'
import { ContractFlowStartPoints, ContractFlowTrackingTypes } from 'types/tracking.ts'
import SmartMeterOption from 'pages/app/addresses/offtake-address/components/smart-meter-option/SmartMeterOption'
import { PendingMeteringChange, SmartMeterOptions, SmartMeterStatus } from 'pages/app/addresses/offtake-address/types'
import { log, setLogContext } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import {
  checkHasSmartMeterOptionsForContract,
  checkIsDynamicTariffEligible,
  checkIsMonthlyBilling,
  checkIsMonthlyBillingEligible,
  getPendingMeteringChanges,
  useGetMeterConfigUpdatedEventLogs
} from 'utils/smartBillingOptions.ts'
import { IS_BETA, IS_PROD_TESTING, WEBSITE_URL } from 'constants/envs.ts'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { setMoveFlow } from 'store/app/slice.ts'
import Link from 'components/link/Link.tsx'
import { AddressDetailProps } from '../types'
import { useNavigate } from 'react-router'
import useWindowSize from 'hooks/useWindowSize'
import { setSelectedContract } from 'store/contact/slice.ts'
import classNames from 'classnames'
import {
  ENERGY_TYPES,
  getProductFromProductCode,
  LANGUAGES,
  MOVE_FLOWS,
  REGIONS,
  SERVICE_CONTRACT_UPDATED_NOTIFICATION_TYPES,
  ServiceContract,
  UpdateServiceContractRequestBody
} from '@boltenergy-be/shared'
import { trackEvent } from 'utils/tracking.ts'
import { boltApi } from 'store/queries/bolt-api'
import { TagTypes } from 'store/queries/bolt-api/types'
import backendLib from 'lib/bolt-backend'
import CurrentTariffsCard from 'components/current-tariffs-card/CurrentTariffsCard.tsx'
import { UserTypes } from 'store/auth/types.ts'

const OfftakeAddressDetails = ({ contract }: AddressDetailProps) => {
  // REDUX STORE
  const { selected, contact } = useStoreSelector((store) => store.contact)
  const { userType } = useStoreSelector((store) => store.auth)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation('addresses')
  const language = useGetLanguage()
  const lowerCaseLanguage = useGetLanguage('lowercase')

  // React router
  const navigate = useNavigate()

  // Custom hooks
  const { isTablet } = useWindowSize()

  // Local state
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false)
  const [isErrored, setIsErrored] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [extraOptionsEditable, setExtraOptionsEditable] = useState<boolean>(false)

  // Constants
  const { serviceContracts } = contract
  const { electricity, gas } = serviceContracts
  const inactiveContract = isInactiveContract(electricity)
  const elProduct = getProductFromProductCode(electricity.detail.productCode)
  const hasSmartMeterOptions = checkHasSmartMeterOptionsForContract(electricity)
  const isContactFromBrussels = electricity?.deliveryPoint?.region === REGIONS.brussels
  const isDynamicTariffEligible = checkIsDynamicTariffEligible({ serviceContracts, product: elProduct })
  const isMonthlyBillingEligible = checkIsMonthlyBillingEligible({ serviceContracts, product: elProduct })
  const isDynamicTariff = electricity.detail.dynamicTariff
  const isMonthlyBilling = checkIsMonthlyBilling(isMonthlyBillingEligible, electricity, gas)
  const containerRef = useRef<HTMLDivElement>(null)
  const showSibelgaInfo = !hasSmartMeterOptions && isContactFromBrussels

  // Local form state
  const [options, setOptions] = useState<{ monthly: boolean; dynamic: boolean }>({
    dynamic: !!isDynamicTariff,
    monthly: !!isMonthlyBilling
  })

  const userEventLogsData = useGetMeterConfigUpdatedEventLogs(contact?.email, userType)

  const pendingMeteringChange = useMemo<PendingMeteringChange | undefined>(() => {
    return getPendingMeteringChanges(userEventLogsData, electricity.contractNumber)
  }, [userEventLogsData, electricity.contractNumber])

  /**
   * Activates or deactivates the monthly billing & dynamic tariff
   */
  const updateSmartMeterOptions = async () => {
    setIsLoading(true)

    // Track event
    trackEvent(
      { category: 'addresses', object: 'smart_meter_options', action: 'update' },
      {
        [SmartMeterOptions.MONTHLY_BILLING]: options.monthly,
        [SmartMeterOptions.DYNAMIC_TARIFF]: options.dynamic
      }
    )

    try {
      const contractsToUpdate: ServiceContract[] = [electricity]

      // Only update gas contract if it exists and is eligible for monthly billing (gas contracts can't have dynamic)
      if (gas && isMonthlyBillingEligible.gas) {
        contractsToUpdate.push(gas)
      }

      setLogContext('contractsToUpdate', { contractsToUpdate })

      const res = await Promise.all(
        contractsToUpdate.map((contract) => {
          // Define body
          const body = {
            detail: {
              dynamicTariff: contract.detail.energyType === ENERGY_TYPES.electricity ? options.dynamic : undefined,
              monthlyBilling: options.monthly
            }
          } satisfies UpdateServiceContractRequestBody

          // Add body to the logging context (for logging if error)
          setLogContext('requestData', body)

          // Return the patch request promise
          return backendLib.updateServiceContract(contract.contractNumber, body)
        })
      )

      if (res.every(({ success }) => success)) {
        // Notify the user
        await backendLib.notifyServiceContractsUpdated(contract.contractNumber, {
          notificationType: SERVICE_CONTRACT_UPDATED_NOTIFICATION_TYPES.smartMeteringUpdate,
          dynamicTariff: electricity.detail.dynamicTariff !== options.dynamic ? options.dynamic : undefined,
          monthlyBilling: electricity.detail.monthlyBilling !== options.monthly ? options.monthly : undefined
        })

        setIsConfirmed(true)
      } else {
        setIsErrored(true)
        resetOptions()

        const msg = res.find(({ message }) => message)?.message

        log({
          error: 'ApiError',
          metaData: {
            data: {
              message: msg
            }
          },
          identifier: `[${Flow.CONTRACT}:patchServiceContract]`
        })
      }
    } catch (error) {
      setIsErrored(true)
      resetOptions()

      log({ error: error as Error, identifier: `[${Flow.CONTRACT}:patchServiceContract]` })
    }

    setConfirmationModalOpen(false)
    setExtraOptionsEditable(false)
    setIsLoading(false)
  }

  /**
   * Resets the options for the contracts
   */
  const resetOptions = () => {
    setOptions({
      dynamic: !!isDynamicTariff,
      monthly: !!isMonthlyBilling
    })
  }

  /**
   * Checks the difference between the current options and the original options
   * @returns {{dynamic: boolean, monthly: boolean}}
   */
  const checkOptionsDifferenceWithOriginal = () => {
    return {
      monthly: isMonthlyBilling !== options.monthly,
      dynamic: isDynamicTariff !== options.dynamic
    }
  }

  return (
    <>
      {/* MY CONNECTIONS BLOCK */}
      <CurrentTariffsCard view={UserTypes.CUSTOMER} {...{ electricity, gas }} />

      {(hasSmartMeterOptions || showSibelgaInfo) && (
        <section className={styles['smart-meter-options']}>
          <header className={styles.header}>
            <Heading as="h5" serif variant="h4">
              {t(($) => $.myContract.smartMeterOptions[showSibelgaInfo ? 'alternativeTitle' : 'title'])}
            </Heading>

            {hasSmartMeterOptions && !showSibelgaInfo && (
              <Button
                representation={extraOptionsEditable ? 'button' : 'link'}
                variant={extraOptionsEditable ? 'primary' : 'secondary'}
                onClick={() => {
                  if (extraOptionsEditable) {
                    const differences = checkOptionsDifferenceWithOriginal()
                    const sameAsOriginal = Object.values(differences).every((value) => !value)

                    if (sameAsOriginal) {
                      setExtraOptionsEditable(false)
                    } else {
                      setConfirmationModalOpen(true)
                    }
                  } else {
                    setExtraOptionsEditable(true)
                  }
                }}
                disabled={!!pendingMeteringChange?.dynamicTariff?.dueDate && !!pendingMeteringChange?.monthlyBilling?.dueDate}
              >
                {t(($) => $.myContract.smartMeterOptions.actions[extraOptionsEditable ? 'save' : 'edit'], {
                  context: isTablet ? 'mobile' : 'desktop'
                })}
              </Button>
            )}
          </header>

          {showSibelgaInfo && (
            <p>
              <Trans
                t={t}
                i18nKey={($) => $.myContract.smartMeterOptions.sibelgaDescription}
                ns="addresses"
                components={[
                  <Link
                    external
                    key="sibelga"
                    href={
                      language === LANGUAGES.FR
                        ? 'https://www.sibelga.be/fr/faq/compteur-intelligent/comment-activer-la-lecture-automatique-des-donnees-du-compteur-intelligent'
                        : 'https://www.sibelga.be/nl/faq/slimme-meter/hoe-activeer-ik-het-automatisch-uitlezen-van-de-gegevens-van-de-slimme-meter'
                    }
                    variant="inline"
                    hideExternalIcon
                  />
                ]}
              />
            </p>
          )}

          {/* DYNAMIC TARIFF */}
          {(isDynamicTariffEligible || showSibelgaInfo) && (
            <SmartMeterOption
              title={t(($) => $.myContract.smartMeterOptions.dynamicTariff.title)}
              option={SmartMeterOptions.DYNAMIC_TARIFF}
              icon={<Icon name="charts" className={classNames(showSibelgaInfo && styles['disabled-icon'])} />}
              status={
                pendingMeteringChange?.dynamicTariff?.active
                  ? SmartMeterStatus.PENDING
                  : options.dynamic
                    ? SmartMeterStatus.ACTIVE
                    : SmartMeterStatus.INACTIVE
              }
              readMoreUrl={`${WEBSITE_URL}/${lowerCaseLanguage}/dynamic-prices`}
              changeDueDate={pendingMeteringChange?.dynamicTariff ? pendingMeteringChange.dynamicTariff.dueDate : undefined}
              onChange={(state) => {
                setOptions({ ...options, dynamic: state })
              }}
              editable={extraOptionsEditable && !pendingMeteringChange?.dynamicTariff?.active}
              isDisabled={showSibelgaInfo}
            />
          )}

          {/* MONTHLY BILLING */}
          {(isMonthlyBillingEligible.electricity || showSibelgaInfo) && (
            <SmartMeterOption
              title={t(($) => $.myContract.smartMeterOptions.monthlyBilling.title[isMonthlyBillingEligible.gas ? 'elAndGas' : 'elOnly'])}
              option={SmartMeterOptions.MONTHLY_BILLING}
              icon={<Icon name="calendar" className={classNames(showSibelgaInfo && styles['disabled-icon'])} />}
              status={
                pendingMeteringChange?.monthlyBilling?.active
                  ? SmartMeterStatus.PENDING
                  : options.monthly
                    ? SmartMeterStatus.ACTIVE
                    : SmartMeterStatus.INACTIVE
              }
              changeDueDate={pendingMeteringChange?.monthlyBilling ? pendingMeteringChange.monthlyBilling.dueDate : undefined}
              editable={extraOptionsEditable && !pendingMeteringChange?.monthlyBilling?.active}
              onChange={(state) => {
                setOptions({ ...options, monthly: state })
              }}
              isDisabled={showSibelgaInfo}
            />
          )}

          {(isConfirmed || isErrored) && (
            <Banner
              type={isErrored ? 'blocking' : 'positive'}
              title={
                isErrored
                  ? t(($) => $.myContract.smartMeterOptions.error.title)
                  : t(($) => $.myContract.smartMeterOptions.confirmation.title)
              }
            >
              <p>
                {parse(
                  isErrored
                    ? t(($) => $.myContract.smartMeterOptions.error.description)
                    : t(($) => $.myContract.smartMeterOptions.confirmation.description)
                )}
              </p>
            </Banner>
          )}
        </section>
      )}

      <section className={styles.actions}>
        {!IS_BETA && !inactiveContract && (
          <Button
            representation="link"
            onClick={() => {
              const flow = MOVE_FLOWS.stop
              trackEvent(
                { category: 'contract', object: 'terminate_contract', action: 'start' },
                {
                  flow,
                  type: ContractFlowTrackingTypes.TERMINATE,
                  from: ContractFlowStartPoints.ADDRESS_DETAIL
                }
              )
              if (contract.contractNumber !== selected.billingContract) {
                dispatch(setSelectedContract(contract.contractNumber))
              }
              dispatch(setMoveFlow(flow))
              void navigate(routes.CONTRACTS_TERMINATE)
            }}
          >
            {t(($) => $.myContract.terminateContract)}
          </Button>
        )}
      </section>

      {/* CONFIRMATION MODAL */}
      <Modal
        title={t(($) => $.myContract.modal.update.title)}
        isOpen={confirmationModalOpen}
        onRequestClose={() => setConfirmationModalOpen(false)}
        customRoot={containerRef.current || undefined}
      >
        {isDynamicTariffEligible && checkOptionsDifferenceWithOriginal().dynamic && (
          <div>
            <Heading as="h3" variant="h6" className="mb-200">
              {t(($) => $.myContract.modal.update.dynamicTariff[options.dynamic ? 'activate' : 'deactivate'].title)}
            </Heading>
            <p>{parse(t(($) => $.myContract.modal.update.dynamicTariff[options.dynamic ? 'activate' : 'deactivate'].description))}</p>
          </div>
        )}

        {isMonthlyBillingEligible && checkOptionsDifferenceWithOriginal().monthly && (
          <div>
            <Heading as="h3" variant="h6" className="mb-200">
              {t(($) => $.myContract.modal.update.monthlyBilling[options.monthly ? 'activate' : 'deactivate'].title)}
            </Heading>
            <p>{parse(t(($) => $.myContract.modal.update.monthlyBilling[options.monthly ? 'activate' : 'deactivate'].description))}</p>
          </div>
        )}

        <p>{parse(t(($) => $.myContract.modal.update.subtext))}</p>

        <div className={styles['actions-group']}>
          <Button
            disabled={IS_PROD_TESTING}
            loading={isLoading}
            onClick={async () => {
              await updateSmartMeterOptions()
              dispatch(boltApi.util.invalidateTags([TagTypes.EVENT_LOGS]))
            }}
          >
            {t(($) => $.myContract.modal.confirm)}
          </Button>

          <Button
            variant="secondary"
            disabled={isLoading}
            onClick={() => {
              setConfirmationModalOpen(false)
            }}
          >
            {t(($) => $.myContract.modal.cancel)}
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default OfftakeAddressDetails
